<template>
  <el-dialog
    title="阅卷规则"
    :visible.sync="dialogVisible"
    width="1160px"
    :close-on-click-modal="false"
    class="setMarkerDialog"
    @close="handleClose"
  >
    <div v-loading="loading" class="dialogBody">
      <div class="modeRow">
        <span class="label">阅卷方式：</span>
        <el-radio-group v-model="markMode" @change="handleModeChange">
          <el-radio :label="1">单人阅卷</el-radio>
          <el-radio :label="2">分题段阅卷</el-radio>
          <el-radio :label="3">重复阅卷</el-radio>
        </el-radio-group>
      </div>

      <!-- 单人阅卷 / 重复阅卷：阅卷人表格 -->
      <el-table
        v-if="markMode === 1 || markMode === 3"
        :data="teacherList"
        border
        max-height="420"
        style="width: 100%"
      >
        <el-table-column type="index" label="序号" width="70" align="center"></el-table-column>
        <el-table-column prop="name" label="姓名" min-width="110" align="center"></el-table-column>
        <el-table-column
          prop="phone"
          label="手机号"
          min-width="130"
          align="center"
        ></el-table-column>
        <el-table-column
          prop="idCard"
          label="身份证号"
          min-width="180"
          align="center"
        ></el-table-column>
        <el-table-column
          prop="createTime"
          label="添加时间"
          min-width="170"
          align="center"
        ></el-table-column>
        <el-table-column label="分配" width="90" align="center">
          <template slot-scope="{ row }">
            <el-switch
              :value="selectedTeacherIds.includes(row.userId)"
              @change="val => handleSwitchChange(row, val)"
            ></el-switch>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分题段阅卷：题目 + 阅卷人卡片 -->
      <div v-if="markMode === 2" class="segmentBox">
        <div ref="questionPanel" class="questionPanel" @mousedown="handlePanelMouseDown">
          <div class="panelTitle">
            <span>全部试题</span>
            <div class="filterBox" @mousedown.stop>
              <span class="filterLabel">筛选未答题题号</span>
              <el-switch v-model="hideUnanswered"></el-switch>
            </div>
          </div>
          <div ref="questionGrid" class="questionGrid" @scroll="hoverTip = null">
            <div
              v-for="q in displayQuestionList"
              :key="q.questionId"
              class="questionItem"
              :class="{ marqueeHit: dragQids.includes(q.questionId) }"
              :style="questionStyle(q)"
              :data-qid="q.questionId"
              @click="handleQuestionClick(q)"
              @mouseenter="handleQuestionEnter(q, $event)"
              @mouseleave="handleQuestionLeave"
            >
              {{ q.num }}
              <span v-if="q.answerCount > 0" class="answerBadge">{{ q.answerCount }}</span>
            </div>
            <el-empty
              v-if="!displayQuestionList.length"
              class="questionEmpty"
              :description="emptyQuestionText"
              :image-size="80"
            ></el-empty>
          </div>
          <div v-if="marqueeRect" class="marqueeBox" :style="marqueeRect"></div>
          <!-- 题号悬浮气泡：题干 + 题号 + 答题人数 -->
          <div v-if="hoverTip" class="questionTip" :style="questionTipStyle">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="tipStem" v-html="hoverTip.question.questionName"></div>
            <div class="tipFooter">
              <span class="tipNum">第 {{ hoverTip.question.num }} 题</span>
              <span class="tipCount">{{ hoverTip.question.answerCount || 0 }} 人答题</span>
            </div>
          </div>
        </div>
        <div class="graderPanel">
          <div
            v-for="(card, idx) in graderCards"
            :key="card.userId"
            class="graderCard"
            :class="{ active: activeCardIndex === idx }"
            @click="activeCardIndex = idx"
          >
            <div class="cardMain">
              <div class="cardHead">
                <span class="cardName">{{ card.userName }}</span>
                <div class="cardQuestions">
                  <span
                    v-for="qid in sortedCardQuestions(card)"
                    :key="qid"
                    class="qBadge"
                    :style="{ background: cardColor(idx) }"
                    >{{ numMap[qid] }}</span
                  >
                </div>
              </div>
              <div class="cardActions">
                <span class="actionLink" @click.stop="handleChangeGrader(idx)">变更</span>
                <span class="actionLink danger" @click.stop="handleRemoveCard(idx)">删除</span>
              </div>
            </div>
            <div class="cardSummary">{{ rangeSummary(card) }}</div>
          </div>
          <div class="addGraderBox">
            <el-button type="primary" @click="handleAddGrader">添加阅卷人</el-button>
          </div>
        </div>
      </div>
    </div>

    <span slot="footer">
      <el-button @click="dialogVisible = false">取 消</el-button>
      <el-button type="primary" :loading="submitLoading" @click="handleSubmit">确 定</el-button>
    </span>

    <!-- 选择阅卷人弹框（添加/变更） -->
    <el-dialog
      :title="pickerMode === 'add' ? '添加阅卷人' : '变更阅卷人'"
      :visible.sync="pickerVisible"
      width="820px"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-table
        ref="pickerTable"
        :data="teacherList"
        border
        max-height="380"
        row-key="userId"
        style="width: 100%"
        @selection-change="handlePickerSelectionChange"
      >
        <el-table-column
          type="selection"
          width="55"
          align="center"
          :selectable="pickerSelectable"
          reserve-selection
        ></el-table-column>
        <el-table-column type="index" label="序号" width="70" align="center"></el-table-column>
        <el-table-column prop="name" label="姓名" min-width="100" align="center"></el-table-column>
        <el-table-column
          prop="phone"
          label="手机号"
          min-width="120"
          align="center"
        ></el-table-column>
        <el-table-column
          prop="idCard"
          label="身份证号"
          min-width="170"
          align="center"
        ></el-table-column>
        <el-table-column
          prop="createTime"
          label="添加时间"
          min-width="160"
          align="center"
        ></el-table-column>
      </el-table>
      <span slot="footer">
        <el-button @click="pickerVisible = false">取 消</el-button>
        <el-button type="primary" @click="handlePickerConfirm">确 定</el-button>
      </span>
    </el-dialog>
  </el-dialog>
</template>
<script>
import to from 'await-to'
import {
  subjectMarkingTeachers,
  subjectMarkingBind,
  subjectMarkingEdit,
  subjectMarkingQuestions,
} from '@/api/exam'
import { userMarkList } from '@/api/system'

// 阅卷人卡片配色
const CARD_COLORS = ['#3671BF', '#E6A23C', '#67C23A', '#9B59B6', '#E66666', '#16A085']

export default {
  name: 'SetMarkerDialog',
  data() {
    return {
      dialogVisible: false,
      pickerVisible: false,
      loading: false,
      submitLoading: false,
      subjectId: '',
      markMode: 1, // 1-单人阅卷 2-分题段阅卷 3-重复阅卷
      teacherList: [], // 全部阅卷人
      selectedTeacherIds: [], // 模式1/3 选中的阅卷人
      hadAssignment: false, // 是否已分配过（决定走 bind 还是 edit）
      questionList: [], // 模式2 题目列表（含序号 num）
      hideUnanswered: false, // 模式2 是否隐藏未答题（answerCount 为 0）的题号
      graderCards: [], // 模式2 阅卷人卡片 [{ userId, userName, questionIds: [] }]
      activeCardIndex: -1, // 当前选中的卡片
      pickerMode: 'add', // add-添加阅卷人 change-变更阅卷人
      pickerSelected: [],
      changeCardIndex: -1,
      marqueeRect: null, // 框选矩形样式
      dragQids: [], // 框选命中的题目id
      dragState: null, // 拖动状态
      suppressClick: false, // 框选结束后屏蔽误触的 click
      hoverTip: null, // 题号悬浮气泡 { question, left, top }
      modeStateCache: {}, // 各模式状态快照，切回时还原老数据
      lastMode: 1, // 上一次所在模式
    }
  },
  computed: {
    // 题目id -> 展示序号
    numMap() {
      const map = {}
      this.questionList.forEach(q => {
        map[q.questionId] = q.num
      })
      return map
    },
    // 题目id -> 所属卡片下标
    questionOwner() {
      const map = {}
      this.graderCards.forEach((card, idx) => {
        card.questionIds.forEach(qid => {
          map[qid] = idx
        })
      })
      return map
    },
    // 气泡定位样式（相对试题卡片，向上展示）
    questionTipStyle() {
      if (!this.hoverTip) return {}
      return {
        left: `${this.hoverTip.left}px`,
        top: `${this.hoverTip.top}px`,
      }
    },
    // 筛选后的题目列表：开关开启时隐藏未答题的题号（answerCount 为 0）
    displayQuestionList() {
      if (!this.hideUnanswered) return this.questionList
      return this.questionList.filter(q => q.answerCount > 0)
    },
    emptyQuestionText() {
      if (this.hideUnanswered && this.questionList.length) return '暂无已答题题号'
      return '暂无需要阅卷的题目'
    },
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.handlePanelMouseMove)
    document.removeEventListener('mouseup', this.handlePanelMouseUp)
  },
  methods: {
    async show(row) {
      this.subjectId = row.id
      this.markMode = 1
      this.lastMode = 1
      this.modeStateCache = {}
      this.selectedTeacherIds = []
      this.graderCards = []
      this.activeCardIndex = -1
      this.questionList = []
      this.hadAssignment = false
      this.marqueeRect = null
      this.dragQids = []
      this.dragState = null
      this.suppressClick = false
      this.hoverTip = null
      this.hideUnanswered = false
      this.dialogVisible = true
      this.loading = true
      const [teacherRes, teacherErr] = await to(userMarkList({}))
      if (teacherErr) {
        this.loading = false
        return this.$message.warning(teacherErr.msg)
      }
      this.teacherList = teacherRes.data || []
      const [res, err] = await to(subjectMarkingTeachers({ subjectId: this.subjectId }))
      this.loading = false
      if (err) return this.$message.warning(err.msg)
      const data = res.data || {}
      const assignedTeachers = (data.teachers || []).filter(t => t.assigned)
      this.hadAssignment = assignedTeachers.length > 0
      this.selectedTeacherIds = assignedTeachers.map(t => t.userId)
      if (data.markMode) this.markMode = data.markMode
      // 已配置为分题段阅卷时，回显阅卷人卡片及题目分配
      if (this.markMode === 2) {
        await this.loadQuestions()
        this.graderCards = assignedTeachers.map(t => ({
          userId: t.userId,
          userName: t.userName,
          questionIds: t.questionIds ? t.questionIds.split(',').map(Number) : [],
        }))
        this.activeCardIndex = this.graderCards.length ? 0 : -1
      }
      this.lastMode = this.markMode
      // 缓存服务端回显的老数据，切换模式后再切回可还原
      this.modeStateCache[this.markMode] = this.snapshotModeState(this.markMode)
    },
    handleClose() {
      this.teacherList = []
      this.hoverTip = null
    },
    async loadQuestions() {
      const [res, err] = await to(subjectMarkingQuestions({ subjectId: this.subjectId }))
      if (err) return this.$message.warning(err.msg)
      const list = (res.data || []).sort((a, b) => a.questionSort - b.questionSort)
      list.forEach((q, i) => {
        q.num = i + 1
      })
      this.questionList = list
    },
    // 快照当前模式状态（单人/重复共用勾选列表，需按模式隔离）
    snapshotModeState(mode) {
      if (mode === 2) {
        return {
          graderCards: this.graderCards.map(c => ({ ...c, questionIds: [...c.questionIds] })),
          activeCardIndex: this.activeCardIndex,
        }
      }
      return { selectedTeacherIds: [...this.selectedTeacherIds] }
    },
    // 还原目标模式的老数据，无缓存则清空初始化，避免跨模式残留
    restoreModeState(mode) {
      const snap = this.modeStateCache[mode]
      if (mode === 2) {
        this.graderCards = snap
          ? snap.graderCards.map(c => ({ ...c, questionIds: [...c.questionIds] }))
          : []
        this.activeCardIndex = snap ? snap.activeCardIndex : -1
      } else {
        this.selectedTeacherIds = snap ? [...snap.selectedTeacherIds] : []
      }
    },
    handleModeChange(mode) {
      // 切换前保存当前模式状态，切换后还原目标模式老数据
      if (this.lastMode !== mode) {
        this.modeStateCache[this.lastMode] = this.snapshotModeState(this.lastMode)
        this.restoreModeState(mode)
        this.lastMode = mode
      }
      if (mode === 2 && !this.questionList.length) this.loadQuestions()
    },
    // 模式1/3 分配开关
    handleSwitchChange(row, val) {
      if (this.markMode === 1) {
        // 单人阅卷只能选择一个
        this.selectedTeacherIds = val ? [row.userId] : []
      } else {
        if (val) {
          this.selectedTeacherIds.push(row.userId)
        } else {
          this.selectedTeacherIds = this.selectedTeacherIds.filter(id => id !== row.userId)
        }
      }
    },
    cardColor(idx) {
      return CARD_COLORS[idx % CARD_COLORS.length]
    },
    questionStyle(q) {
      const owner = this.questionOwner[q.questionId]
      if (owner === undefined) return {}
      const color = this.cardColor(owner)
      return { background: color, borderColor: color, color: '#ffffff' }
    },
    // 点击题号：分配/取消分配给当前选中的阅卷人
    handleQuestionClick(q) {
      if (this.suppressClick) return
      const card = this.graderCards[this.activeCardIndex]
      if (!card) return this.$message.warning('请先选中右侧阅卷人')
      const owner = this.questionOwner[q.questionId]
      if (owner === undefined) {
        card.questionIds.push(q.questionId)
      } else if (owner === this.activeCardIndex) {
        card.questionIds = card.questionIds.filter(id => id !== q.questionId)
      } else {
        this.$message.warning(`该题已分配给【${this.graderCards[owner].userName}】`)
      }
    },
    // 悬浮题号：向上展示气泡；框选拖动期间不展示
    handleQuestionEnter(q, e) {
      if (this.dragState) return
      const item = e.currentTarget
      const panel = this.$refs.questionPanel
      if (!item || !panel) return
      const panelRect = panel.getBoundingClientRect()
      const itemRect = item.getBoundingClientRect()
      const centerX = itemRect.left - panelRect.left - panel.clientLeft + itemRect.width / 2
      const halfTip = 110 // 气泡最大宽度的一半，贴边时收拢避免溢出
      this.hoverTip = {
        question: q,
        left: Math.min(Math.max(centerX, halfTip), Math.max(halfTip, panel.clientWidth - halfTip)),
        top: itemRect.top - panelRect.top - panel.clientTop,
      }
    },
    handleQuestionLeave() {
      this.hoverTip = null
    },
    // 框选（类似 Windows 拖拉选择文件）：在整个试题卡片内按下左键拖动，命中题目批量分配给当前阅卷人；按住 Ctrl 拖动为反选
    handlePanelMouseDown(e) {
      if (e.button !== 0 || !this.questionList.length) return
      const panel = this.$refs.questionPanel
      if (!panel) return
      e.preventDefault()
      this.hoverTip = null // 按下即隐藏气泡，避免拖动过程中弹泡
      const rect = panel.getBoundingClientRect()
      this.dragState = {
        startX: e.clientX - rect.left - panel.clientLeft,
        startY: e.clientY - rect.top - panel.clientTop,
        active: false,
        ctrlKey: e.ctrlKey, // Ctrl+拖动：反选命中题目
      }
      document.addEventListener('mousemove', this.handlePanelMouseMove)
      document.addEventListener('mouseup', this.handlePanelMouseUp)
    },
    handlePanelMouseMove(e) {
      const panel = this.$refs.questionPanel
      const grid = this.$refs.questionGrid
      const state = this.dragState
      if (!panel || !grid || !state) return
      const rect = panel.getBoundingClientRect()
      const x = e.clientX - rect.left - panel.clientLeft
      const y = e.clientY - rect.top - panel.clientTop
      // 移动超过阈值才视为拖动，避免与单击冲突
      if (!state.active) {
        if (Math.abs(x - state.startX) < 4 && Math.abs(y - state.startY) < 4) return
        state.active = true
      }
      const box = {
        left: Math.min(state.startX, x),
        top: Math.min(state.startY, y),
        width: Math.abs(x - state.startX),
        height: Math.abs(y - state.startY),
      }
      // 选框保持真实大小，不限制在卡片范围内
      this.marqueeRect = {
        left: `${box.left}px`,
        top: `${box.top}px`,
        width: `${box.width}px`,
        height: `${box.height}px`,
      }
      this.updateDragHits(grid, box)
    },
    updateDragHits(grid, box) {
      const hits = []
      grid.querySelectorAll('.questionItem').forEach(el => {
        // 题目坐标换算到卡片坐标系（补偿网格自身滚动距离）
        const l = grid.offsetLeft + el.offsetLeft
        const t = grid.offsetTop + el.offsetTop - grid.scrollTop
        const hit =
          l < box.left + box.width &&
          l + el.offsetWidth > box.left &&
          t < box.top + box.height &&
          t + el.offsetHeight > box.top
        if (hit) hits.push(Number(el.dataset.qid))
      })
      this.dragQids = hits
    },
    handlePanelMouseUp() {
      document.removeEventListener('mousemove', this.handlePanelMouseMove)
      document.removeEventListener('mouseup', this.handlePanelMouseUp)
      const state = this.dragState
      this.dragState = null
      if (!state || !state.active) return
      this.suppressClick = true
      setTimeout(() => {
        this.suppressClick = false
      }, 0)
      const qids = this.dragQids
      this.marqueeRect = null
      this.dragQids = []
      if (!qids.length) return
      const card = this.graderCards[this.activeCardIndex]
      if (!card) return this.$message.warning('请先选中右侧阅卷人')
      if (state.ctrlKey) {
        // Ctrl+拖动：反选，对当前卡片的分配状态取反（他人已分配的不动）
        let added = 0
        let removed = 0
        let skipped = 0
        qids.forEach(qid => {
          const owner = this.questionOwner[qid]
          if (owner === this.activeCardIndex) {
            card.questionIds = card.questionIds.filter(id => id !== qid)
            removed += 1
          } else if (owner === undefined) {
            card.questionIds.push(qid)
            added += 1
          } else {
            skipped += 1
          }
        })
        if (added && removed) this.$message.success(`已反选：新增 ${added} 道，取消 ${removed} 道`)
        else if (added) this.$message.success(`已反选：新增 ${added} 道题`)
        else if (removed) this.$message.success(`已反选：取消 ${removed} 道题`)
        else if (skipped) this.$message.warning('所选题目已分配给其他阅卷人')
        else this.$message.warning('没有可反选的题目')
        return
      }
      let assigned = 0
      let skipped = 0
      qids.forEach(qid => {
        const owner = this.questionOwner[qid]
        if (owner === undefined) {
          card.questionIds.push(qid)
          assigned += 1
        } else if (owner !== this.activeCardIndex) {
          skipped += 1
        }
      })
      if (assigned && skipped) {
        this.$message.success(
          `已为【${card.userName}】分配 ${assigned} 道题，${skipped} 道题已被其他阅卷人分配，已跳过`,
        )
      } else if (assigned) {
        this.$message.success(`已为【${card.userName}】分配 ${assigned} 道题`)
      } else if (skipped) {
        this.$message.warning('所选题目已分配给其他阅卷人')
      }
    },
    sortedCardQuestions(card) {
      return [...card.questionIds].sort((a, b) => (this.numMap[a] || 0) - (this.numMap[b] || 0))
    },
    // 题号区间汇总，如 33-35、38、40
    rangeSummary(card) {
      const nums = this.sortedCardQuestions(card)
        .map(qid => this.numMap[qid])
        .filter(Boolean)
      if (!nums.length) return ''
      const parts = []
      let start = nums[0]
      let prev = nums[0]
      for (let i = 1; i <= nums.length; i++) {
        if (nums[i] === prev + 1) {
          prev = nums[i]
          continue
        }
        parts.push(start === prev ? `${start}` : `${start}-${prev}`)
        start = nums[i]
        prev = nums[i]
      }
      return parts.join('、')
    },
    handleAddGrader() {
      this.pickerMode = 'add'
      this.pickerSelected = []
      this.pickerVisible = true
      this.$nextTick(() => {
        if (this.$refs.pickerTable) this.$refs.pickerTable.clearSelection()
      })
    },
    handleChangeGrader(idx) {
      this.changeCardIndex = idx
      this.pickerMode = 'change'
      this.pickerSelected = []
      this.pickerVisible = true
      this.$nextTick(() => {
        if (this.$refs.pickerTable) this.$refs.pickerTable.clearSelection()
      })
    },
    handleRemoveCard(idx) {
      this.$confirm('确定删除该阅卷人及其题目分配吗？', '提示', {
        type: 'warning',
        confirmButtonText: '确定',
        cancelButtonText: '取消',
      })
        .then(() => {
          this.graderCards.splice(idx, 1)
          if (this.activeCardIndex === idx) {
            this.activeCardIndex = this.graderCards.length ? 0 : -1
          } else if (this.activeCardIndex > idx) {
            this.activeCardIndex -= 1
          }
        })
        .catch(() => {})
    },
    // 已选择过的人员置灰不可再选（变更时原人员同样置灰）
    pickerSelectable(row) {
      return !this.graderCards.some(c => c.userId === row.userId)
    },
    handlePickerSelectionChange(selection) {
      // 变更为单选：保留最新选择的一行
      if (this.pickerMode === 'change' && selection.length > 1) {
        const latest = selection[selection.length - 1]
        this.$refs.pickerTable.clearSelection()
        this.$nextTick(() => {
          this.$refs.pickerTable.toggleRowSelection(latest, true)
        })
        this.pickerSelected = [latest]
        return
      }
      this.pickerSelected = selection
    },
    handlePickerConfirm() {
      if (!this.pickerSelected.length) return this.$message.warning('请选择阅卷人')
      if (this.pickerMode === 'add') {
        const startIndex = this.graderCards.length
        this.pickerSelected.forEach(t => {
          this.graderCards.push({ userId: t.userId, userName: t.name, questionIds: [] })
        })
        this.activeCardIndex = startIndex
      } else {
        const t = this.pickerSelected[0]
        const card = this.graderCards[this.changeCardIndex]
        if (card) {
          card.userId = t.userId
          card.userName = t.name
        }
      }
      this.pickerVisible = false
    },
    async handleSubmit() {
      let assignments = []
      if (this.markMode === 1) {
        if (this.selectedTeacherIds.length !== 1) {
          return this.$message.warning('请选择一名阅卷人')
        }
        assignments = [{ userId: this.selectedTeacherIds[0], questionIds: null }]
      } else if (this.markMode === 3) {
        if (this.selectedTeacherIds.length < 2) {
          return this.$message.warning('重复阅卷请至少选择两名阅卷人')
        }
        assignments = this.selectedTeacherIds.map(userId => ({ userId, questionIds: null }))
      } else {
        if (!this.graderCards.length) return this.$message.warning('请添加阅卷人')
        if (this.graderCards.some(c => !c.questionIds.length)) {
          return this.$message.warning('请为每位阅卷人分配需要批改的题目')
        }
        assignments = this.graderCards.map(c => ({
          userId: c.userId,
          questionIds: c.questionIds.join(','),
        }))
      }
      if (this.submitLoading) return
      this.submitLoading = true
      const api = this.hadAssignment ? subjectMarkingEdit : subjectMarkingBind
      const [, err] = await to(
        api({ subjectId: this.subjectId, markMode: this.markMode, assignments }),
      )
      this.submitLoading = false
      if (err) return this.$message.warning(err.msg)
      this.$message.success('设置成功')
      this.dialogVisible = false
      this.$emit('refresh')
    },
  },
}
</script>
<style scoped lang="scss">
.setMarkerDialog {
  .dialogBody {
    min-height: 300px;
    .modeRow {
      display: flex;
      align-items: center;
      margin-bottom: 20px;
      .label {
        font-size: 14px;
        color: #333333;
      }
    }
  }
  .segmentBox {
    display: flex;
    align-items: flex-start;
    .questionPanel {
      position: relative;
      width: 380px;
      flex-shrink: 0;
      border: 1px solid #ebeef5;
      border-radius: 4px;
      padding: 16px;
      .panelTitle {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 14px;
        color: #333333;
        margin-bottom: 12px;
        // 筛选未答题题号开关
        .filterBox {
          display: flex;
          align-items: center;
          .filterLabel {
            margin-right: 6px;
            font-size: 12px;
            color: #909399;
          }
        }
      }
      .questionGrid {
        position: relative;
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        grid-gap: 10px;
        max-height: 460px;
        overflow-y: auto;
        // 预留角标溢出空间并用负 margin 补偿，避免首行/末列角标被裁剪
        padding: 8px 8px 0 0;
        margin: -8px -8px 0 0;
        .questionEmpty {
          grid-column: 1 / -1;
          justify-self: center;
        }
        .questionItem {
          position: relative;
          height: 44px;
          line-height: 44px;
          text-align: center;
          font-size: 14px;
          color: #606266;
          border: 1px solid #dcdfe6;
          border-radius: 4px;
          cursor: pointer;
          user-select: none;
          &:hover {
            border-color: #3671bf;
          }
          // 答题人数角标
          .answerBadge {
            position: absolute;
            top: -8px;
            right: -8px;
            z-index: 2;
            min-width: 18px;
            height: 18px;
            padding: 0 4px;
            box-sizing: border-box;
            border-radius: 9px;
            background: #f56c6c;
            color: #ffffff;
            font-size: 12px;
            line-height: 18px;
            text-align: center;
            pointer-events: none;
          }
          // 框选命中预览
          &.marqueeHit::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            border-radius: 4px;
            background: rgba(54, 113, 191, 0.3);
          }
        }
      }
      // Windows 风格框选矩形（绝对定位于 questionPanel，不占文档流）
      .marqueeBox {
        position: absolute;
        background: rgba(54, 113, 191, 0.12);
        border: 1px solid #3671bf;
        pointer-events: none;
        z-index: 10;
      }
      // 题号悬浮气泡
      .questionTip {
        position: absolute;
        z-index: 20;
        width: 220px;
        transform: translate(-50%, calc(-100% - 8px));
        background: #ffffff;
        border: 1px solid #ebeef5;
        border-radius: 4px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        padding: 10px 12px;
        pointer-events: none;
        .tipStem {
          font-size: 13px;
          color: #333333;
          line-height: 20px;
          word-break: break-all;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          max-height: 40px;
        }
        .tipFooter {
          margin-top: 8px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          .tipNum {
            font-size: 12px;
            color: #909399;
          }
          .tipCount {
            font-size: 12px;
            color: #333333;
            background: #f5f7fa;
            border-radius: 2px;
            padding: 2px 6px;
          }
        }
      }
    }
    .graderPanel {
      flex: 1;
      margin-left: 20px;
      .graderCard {
        display: flex;
        border: 1px solid #ebeef5;
        border-radius: 4px;
        margin-bottom: 16px;
        cursor: pointer;
        &.active {
          border-color: #3671bf;
          box-shadow: 0 0 0 1px #3671bf;
        }
        .cardMain {
          flex: 1;
          padding: 16px;
          .cardHead {
            display: flex;
            align-items: flex-start;
            .cardName {
              font-size: 15px;
              color: #333333;
              font-weight: 500;
              margin-right: 20px;
              line-height: 32px;
              flex-shrink: 0;
            }
            .cardQuestions {
              display: flex;
              flex-wrap: wrap;
              .qBadge {
                min-width: 32px;
                height: 32px;
                line-height: 32px;
                text-align: center;
                border-radius: 4px;
                color: #ffffff;
                font-size: 14px;
                margin: 0 8px 8px 0;
                padding: 0 4px;
              }
            }
          }
          .cardActions {
            margin-top: 10px;
            .actionLink {
              display: inline-block;
              font-size: 14px;
              color: #3671bf;
              margin-right: 16px;
              &.danger {
                color: #f56c6c;
              }
            }
          }
        }
        .cardSummary {
          width: 110px;
          flex-shrink: 0;
          border-left: 1px solid #ebeef5;
          padding: 16px 10px;
          font-size: 14px;
          color: #606266;
          word-break: break-all;
        }
      }
      .addGraderBox {
        border: 1px dashed #dcdfe6;
        border-radius: 4px;
        height: 90px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
}
</style>
